import { capitalize, get } from 'lodash';
import Grid from '@lendi-ui/grid';

import { ASSET_TYPE, EXPENSE_TYPE, FIELD_PATHS, FIELD_VALUES, PII_FIELD_MASKING_CLASS } from '@funnels/constants';
import { Asset, FormValues } from '@funnels/types';
import { StringDisplay } from '@funnels/ui-components/Fields';
import { getEmploymentTypeAndIndex, getExpensesIndex, getExpenseTypeIndex } from '@funnels/utils';

// WHILE THE NAME IS A HARDCODED DEMO
import { PanelText } from '../../../Fields/PlainDisplay/style';
import {
  AnnualIncomeReadOnlyView,
  DependantsNumberReadOnlyView,
  EmploymentTypeReadOnlyView,
  FirstHomeOwnersGrantEligibleReadOnlyView,
  MonthlyLivingExpensesReadOnlyView,
} from '../../../Questions';
import { Divider, PaddedGridUnit } from '../_common/style';

interface ConstructionLoanLayoutProps {
  index: number;
  currentValues: FormValues;
}

export const ConstructionLoanLayout = (props: ConstructionLoanLayoutProps) => {
  const { currentValues, index } = props;
  const applicantFirstHomeBuyerValue = get(currentValues, FIELD_PATHS.APPLICANT_IS_FIRST_HOME_BUYER(index));
  const hasNPProperty = get(currentValues, FIELD_PATHS.ASSETS(), []).some(
    (asset: Asset) =>
      get({ assets: [asset] }, FIELD_PATHS.ASSET_TO_BE_PURCHASED(0)) === true &&
      get({ assets: [asset] }, FIELD_PATHS.ASSET_TYPE(0)) === ASSET_TYPE.PROPERTY
  );
  const { employmentIndex } = getEmploymentTypeAndIndex(index, currentValues);

  return (
    <Grid gutter="nil">
      {index > 0 && (
        <PaddedGridUnit size={1}>
          <Divider />
        </PaddedGridUnit>
      )}
      <PaddedGridUnit size={1}>
        <PanelText className={PII_FIELD_MASKING_CLASS}>
          {get(currentValues, FIELD_PATHS.APPLICANT_FIRST_NAME(index), `Applicant ${index + 1}`)}
        </PanelText>
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <EmploymentTypeReadOnlyView
          value={get(currentValues, FIELD_PATHS.EMPLOYMENT_EMPLOYMENT_TYPE(employmentIndex), undefined)}
        />
      </PaddedGridUnit>
      {hasNPProperty && (
        <PaddedGridUnit size={1 / 2}>
          <FirstHomeOwnersGrantEligibleReadOnlyView
            value={
              applicantFirstHomeBuyerValue === true
                ? FIELD_VALUES.YES
                : applicantFirstHomeBuyerValue === false
                ? FIELD_VALUES.NO
                : undefined
            }
            title={'First home buyer'}
          />
        </PaddedGridUnit>
      )}
    </Grid>
  );
};

export const NewPurchaseLayout = ConstructionLoanLayout;
export const RefinanceLayout = ConstructionLoanLayout;

interface PreapprovalLayoutProps {
  index: number;
  currentValues: FormValues;
}

export const PreapprovalLayout = (props: PreapprovalLayoutProps) => {
  const { currentValues, index } = props;
  const applicantFirstHomeBuyerValue = get(currentValues, FIELD_PATHS.APPLICANT_IS_FIRST_HOME_BUYER(index));
  const hasNPProperty = get(currentValues, FIELD_PATHS.ASSETS(), []).some(
    (asset: Asset) =>
      get({ assets: [asset] }, FIELD_PATHS.ASSET_TO_BE_PURCHASED(0)) === true &&
      get({ assets: [asset] }, FIELD_PATHS.ASSET_TYPE(0)) === ASSET_TYPE.PROPERTY
  );
  const { employmentIndex } = getEmploymentTypeAndIndex(index, currentValues);
  const expensesIndex = getExpensesIndex(index, currentValues);
  const expenseTypeIndex = getExpenseTypeIndex(expensesIndex, currentValues, EXPENSE_TYPE.LIVING_EXPENSES_ESTIMATE);
  return (
    <Grid gutter="nil">
      {index > 0 && (
        <PaddedGridUnit size={1}>
          <Divider />
        </PaddedGridUnit>
      )}
      <PaddedGridUnit size={1}>
        <PanelText className={PII_FIELD_MASKING_CLASS}>
          {get(currentValues, FIELD_PATHS.APPLICANT_FIRST_NAME(index), `Applicant ${index + 1}`)}
        </PanelText>
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <EmploymentTypeReadOnlyView
          value={get(currentValues, FIELD_PATHS.EMPLOYMENT_EMPLOYMENT_TYPE(employmentIndex), undefined)}
        />
      </PaddedGridUnit>
      {hasNPProperty && (
        <PaddedGridUnit size={1 / 2}>
          <FirstHomeOwnersGrantEligibleReadOnlyView
            value={
              applicantFirstHomeBuyerValue === true
                ? FIELD_VALUES.YES
                : applicantFirstHomeBuyerValue === false
                ? FIELD_VALUES.NO
                : undefined
            }
            title={'First home buyer'}
          />
        </PaddedGridUnit>
      )}
      <PaddedGridUnit size={1 / 2}>
        <AnnualIncomeReadOnlyView
          title="Income"
          value={get(currentValues, FIELD_PATHS.APPLICANT_GROSS_SALARY_AMOUNT(index), undefined)}
        />
      </PaddedGridUnit>
      <PaddedGridUnit size={1}>
        <Divider />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <DependantsNumberReadOnlyView
          value={get(currentValues, FIELD_PATHS.APPLICATION_NUMBER_OF_DEPENDANTS(0), undefined)}
        />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <MonthlyLivingExpensesReadOnlyView
          value={get(currentValues, FIELD_PATHS.EXPENSES_EXPENSE_AMOUNT(expensesIndex, expenseTypeIndex), undefined)}
        />
      </PaddedGridUnit>
    </Grid>
  );
};

interface NonPersonApplicantLayoutProps {
  applicantEntity: FIELD_VALUES.TRUST | FIELD_VALUES.COMPANY;
  applicantName: string;
  applicantIndex: number;
}

export const NonPersonApplicantLayout = ({
  applicantEntity,
  applicantName,
  applicantIndex,
}: NonPersonApplicantLayoutProps) => {
  return (
    <Grid gutter="nil">
      {applicantIndex > 0 && (
        <PaddedGridUnit size={1}>
          <Divider />
        </PaddedGridUnit>
      )}
      <PaddedGridUnit size={1}>
        <PanelText className={PII_FIELD_MASKING_CLASS}>{capitalize(applicantName)}</PanelText>
      </PaddedGridUnit>
      <PaddedGridUnit size={1}>
        <StringDisplay title="Applicant type" value={applicantEntity} />
      </PaddedGridUnit>
    </Grid>
  );
};
